// src/components/widgets/AsistenciaHoyWidget.js
// Widget "Asistencia Hoy" — pulso del día para gestores: cuántos colaboradores
// se esperan, quiénes están trabajando, en almuerzo, ya salieron, quiénes SIN
// MARCAR todavía, y cuántas marcas esperan revisión.
// Lee 1 doc precalculado (`widgets_datos/asistencia_hoy`, lo arma el cron
// cronAsistenciaHoyWidget cada hora de 7:00 a 19:00 GT, lun-vie) — así
// quien tiene el permiso del widget lo ve aunque no tenga el módulo
// Asistencia (las reglas de asistencia_marcas no dejarían consultar directo).
// Se muestra por PERMISO (`widget-asistencia-hoy`, sección WIDGETS de Permisos).
// El detalle fino (tardanzas, horarios, fotos) vive en el módulo /asistencia.
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { UsersIcon, ClockIcon, CheckCircleIcon, ExclamationTriangleIcon } from '../ui/Icons';
import useEsMovil from '../../hooks/useEsMovil';
import useConteoAnimado from '../../hooks/useConteoAnimado';
import { ESTILO_TILE } from './widgetsUI';

const estilosResponsivos = `
  @media (max-width: 768px) {
    .asis-widget-container { margin: 15px 0 !important; }
    .asis-header {
      flex-direction: column !important;
      align-items: center !important;
      text-align: center;
      margin-bottom: 12px !important;
    }
    .asis-header h3 { font-size: 18px !important; margin: 0 !important; }
    .asis-header-der { width: 100%; flex-direction: column !important; gap: 8px !important; }
    .btn-abrir-asistencia { width: 100% !important; }
    .asis-tiles { grid-template-columns: 1fr 1fr !important; gap: 8px !important; }
    .asis-tile { padding: 10px !important; }
    .asis-nombres-grid { grid-template-columns: 1fr !important; gap: 10px !important; }
    .asis-card { padding: 12px !important; }
  }
`;

const hoyGT = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Guatemala' }).format(new Date());

const formatearHora = (timestamp) => {
  if (!timestamp) return '';
  try {
    let fecha;
    if (timestamp.toDate && typeof timestamp.toDate === 'function') fecha = timestamp.toDate();
    else if (timestamp.seconds) fecha = new Date(timestamp.seconds * 1000);
    else fecha = new Date(timestamp);
    if (isNaN(fecha.getTime())) return '';
    return fecha.toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'America/Guatemala' });
  } catch (e) {
    return '';
  }
};

// Grupo de nombres (columna con chips) reutilizado 3 veces
function GrupoNombres({ titulo, color, nombres }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: '12px', fontWeight: 'bold', color, marginBottom: '6px' }}>
        {titulo} ({nombres.length})
      </div>
      {nombres.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
          {nombres.map((n) => (
            <span key={n} style={{
              fontSize: '12px',
              backgroundColor: 'var(--color-fondo)',
              border: '1px solid var(--color-borde-suave)',
              borderRadius: 'var(--radio-sm)',
              padding: '2px 8px',
              color: 'var(--color-texto)'
            }}>
              {n}
            </span>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)' }}>Nadie por ahora</div>
      )}
    </div>
  );
}

// conEnlace: el botón "Abrir Asistencia" sale solo si la persona puede abrir el módulo (candado de ruta de
// Layout.js, lo decide Inicio): el permiso del widget no da el módulo.
function AsistenciaHoyWidget({ conEnlace = false }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const isMobile = useEsMovil();

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'widgets_datos', 'asistencia_hoy'));
        if (vivo) setDatos(snap.exists() ? snap.data() : null);
      } catch (e) {
        console.error('Error cargando asistencia de hoy:', e);
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
  }, []);

  const esDeHoy = datos?.fecha === hoyGT();
  const trabajando = (esDeHoy && datos?.trabajando) || [];
  const enAlmuerzo = (esDeHoy && datos?.enAlmuerzo) || [];
  const salieron = (esDeHoy && datos?.salieron) || [];
  const sinMarcar = (esDeHoy && datos?.sinMarcar) || [];
  const revision = (esDeHoy && datos?.revisionPendientes) || 0;
  const totalColaboradores = (esDeHoy && datos?.totalColaboradores) || 0;

  // Conteos animados (los hooks van antes de cualquier return)
  const colaboradoresAnimado = useConteoAnimado(totalColaboradores);
  const almuerzoAnimado = useConteoAnimado(enAlmuerzo.length);
  const salieronAnimado = useConteoAnimado(salieron.length);
  const revisionAnimado = useConteoAnimado(revision);

  if (cargando) {
    return (
      <>
        <style>{estilosResponsivos}</style>
        <div className="asis-widget-container" style={{ maxWidth: '1200px', margin: isMobile ? '15px 0' : '30px auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: isMobile ? '12px' : '16px' }}>
            <div className="skeleton" style={{ width: '220px', height: '24px', borderRadius: 'var(--radio-sm)' }} />
            <div className="skeleton" style={{ width: '260px', height: '34px', borderRadius: 'var(--radio-sm)' }} />
          </div>
          <div className="asis-tiles" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))', gap: isMobile ? '8px' : '14px', marginBottom: '14px' }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="skeleton" style={{ height: '84px', borderRadius: 'var(--radio-lg)' }} />
            ))}
          </div>
          <div className="skeleton" style={{ width: '100%', height: '110px', borderRadius: 'var(--radio-lg)' }} />
        </div>
      </>
    );
  }

  // El cron todavía no armó ningún doc
  if (!datos) return null;

  return (
    <>
      <style>{estilosResponsivos}</style>
      <div className="asis-widget-container" style={{ maxWidth: '1200px', margin: isMobile ? '15px 0' : '30px auto' }}>
        {/* Header estilo dashboard */}
        <div className="asis-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px',
          marginBottom: isMobile ? '12px' : '16px'
        }}>
          <h3 style={{ margin: 0, fontSize: isMobile ? '18px' : '22px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: 'var(--color-primario-fuerte)', display: 'inline-flex' }}><UsersIcon className="w-6 h-6" /></span>
            Asistencia Hoy
          </h3>
          <div className="asis-header-der" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {esDeHoy && datos.actualizadoEn && (
              <small
                title="El resumen se recalcula cada hora entre 7:00 y 19:00, de lunes a viernes"
                style={{ color: 'var(--color-texto-secundario)', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
              >
                <span style={{ display: 'inline-flex' }}><ClockIcon className="w-3 h-3" /></span>
                Actualizado {formatearHora(datos.actualizadoEn)} · cada hora
              </small>
            )}
            {conEnlace && (
            <Link
              to="/asistencia"
              className="btn-abrir-asistencia btn-press hover-bright"
              style={{
                height: '38px',
                boxSizing: 'border-box',
                padding: '0 20px',
                backgroundColor: 'var(--color-primario)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radio-sm)',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                textDecoration: 'none'
              }}
            >
              <ClockIcon className="w-4 h-4" /> Abrir Asistencia
            </Link>
            )}
          </div>
        </div>

        {!esDeHoy ? (
          // Doc de otro día: el cron corre de 7:00 a 19:00 — de madrugada aún no hay datos
          <div className="card asis-card" style={{ ...ESTILO_TILE, fontSize: '13px', color: 'var(--color-texto-secundario)' }}>
            Todavía no hay resumen de hoy. Se arma cada hora entre 7:00 y 19:00 (lunes a viernes).
          </div>
        ) : (
          <>
            {/* Fila de KPI tiles */}
            <div className="asis-tiles stagger-list" style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))',
              gap: isMobile ? '8px' : '14px',
              marginBottom: isMobile ? '10px' : '14px'
            }}>
              <div className="card asis-tile" style={ESTILO_TILE} title="Colaboradores con control de asistencia (excluidos no cuentan). OJO: 'sin marcar' no descuenta vacaciones ni días de descanso — el detalle exacto está en el módulo Asistencia">
                <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Colaboradores</div>
                <div style={{ fontSize: '26px', fontWeight: 'bold', color: 'var(--color-texto)', fontVariantNumeric: 'tabular-nums' }}>
                  {Math.round(colaboradoresAnimado)}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
                  <span style={{ color: 'var(--color-exito-texto)', fontWeight: 'bold' }}>{trabajando.length} trabajando</span>
                  {' · '}
                  <span style={sinMarcar.length > 0 ? { color: 'var(--color-advertencia-texto)', fontWeight: 'bold' } : undefined}>
                    {sinMarcar.length} sin marcar
                  </span>
                </div>
              </div>

              <div className="card asis-tile" style={ESTILO_TILE} title="Colaboradores que marcaron inicio de almuerzo y todavía no marcan el regreso">
                <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>En almuerzo</div>
                <div style={{ fontSize: '26px', fontWeight: 'bold', color: 'var(--color-texto)', fontVariantNumeric: 'tabular-nums' }}>
                  {Math.round(almuerzoAnimado)}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
                  {enAlmuerzo.length === 1 ? 'colaborador afuera' : 'colaboradores afuera'}
                </div>
              </div>

              <div className="card asis-tile" style={ESTILO_TILE} title="Colaboradores que ya marcaron su salida del día">
                <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Ya salieron</div>
                <div style={{ fontSize: '26px', fontWeight: 'bold', color: 'var(--color-texto)', fontVariantNumeric: 'tabular-nums' }}>
                  {Math.round(salieronAnimado)}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
                  jornada terminada
                </div>
              </div>

              <div className="card asis-tile" style={ESTILO_TILE} title="Marcas del día donde la cámara no confirmó el rostro o vienen con bandera del celular — se validan en Asistencia → Revisión">
                <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Para revisión</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '26px', fontWeight: 'bold', color: revision > 0 ? 'var(--color-advertencia-texto)' : 'var(--color-texto)', fontVariantNumeric: 'tabular-nums' }}>
                    {Math.round(revisionAnimado)}
                  </span>
                  <span style={{ display: 'inline-flex', color: revision > 0 ? 'var(--color-advertencia-texto)' : 'var(--color-exito)' }}>
                    {revision > 0 ? <ExclamationTriangleIcon className="w-5 h-5" /> : <CheckCircleIcon className="w-5 h-5" />}
                  </span>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
                  {revision > 0 ? 'marcas por validar' : 'todo validado'}
                </div>
              </div>
            </div>

            {/* Quiénes: nombres por estado */}
            <div className="card asis-card" style={ESTILO_TILE}>
              <div className="asis-nombres-grid" style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, minmax(0, 1fr))',
                gap: isMobile ? '10px' : '16px'
              }}>
                <GrupoNombres titulo="Trabajando" color="var(--color-exito-texto)" nombres={trabajando} />
                <GrupoNombres titulo="En almuerzo" color="var(--color-info-texto)" nombres={enAlmuerzo} />
                <GrupoNombres titulo="Ya salieron" color="var(--color-texto-secundario)" nombres={salieron} />
                <GrupoNombres titulo="Sin marcar" color="var(--color-advertencia-texto)" nombres={sinMarcar} />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default AsistenciaHoyWidget;
